import { QUALITY_THRESHOLDS } from './constants';
import type { QualityIndicator } from '../types';

type MetricName = keyof typeof QUALITY_THRESHOLDS;

/**
 * Determine quality indicator where higher values are better (delivery, open, click)
 */
export function getQualityIndicator(metric: MetricName, value: number): QualityIndicator {
  const thresholds = QUALITY_THRESHOLDS[metric];

  if (thresholds == null) {
    return 'good';
  }

  if (value >= thresholds.good) {
    return 'good';
  } else if (value >= thresholds.needs_work) {
    return 'needs_work';
  }

  return 'critical';
}

/**
 * Determine quality indicator where lower values are better (bounce, spam)
 */
export function getInverseQualityIndicator(metric: MetricName, value: number): QualityIndicator {
  const thresholds = QUALITY_THRESHOLDS[metric];

  if (thresholds == null) {
    return 'good';
  }

  if (value < thresholds.good) {
    return 'good';
  } else if (value < thresholds.needs_work) {
    return 'needs_work';
  }

  return 'critical';
}

/**
 * Get quality color for Pluma Text component
 */
export function getQualityColor(quality: QualityIndicator): 'inherit' | 'critical' | 'caution' {
  if (quality === 'critical') return 'critical';
  if (quality === 'needs_work') return 'caution';
  return 'inherit';
}

/**
 * Generate tooltip message for quality indicators
 */
export function getQualityMessage(
  metricName: MetricName,
  quality: QualityIndicator,
  isInverse: boolean,
  precision: number,
): string | undefined {
  const indicator = QUALITY_THRESHOLDS[metricName];
  if (indicator == null || quality === 'good') {
    return undefined;
  }

  const directionTerm = isInverse ? 'below' : 'above';
  const formatPercent = (val: number) => `${(val * 100).toFixed(precision)}%`;

  if (quality === 'needs_work') {
    return `${indicator.label} needs improvement. Target it ${directionTerm} ${formatPercent(indicator.good)}.`;
  }

  if (quality === 'critical') {
    return `${indicator.label} is critical. Target it ${directionTerm} ${formatPercent(
      indicator.needs_work,
    )}. Ideally it should be ${directionTerm} ${formatPercent(indicator.good)}.`;
  }

  return undefined;
}
